import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Settings, Users, Building2, CheckCircle2, Calendar, DollarSign, Loader2 } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * Component for managing auto-assignment rules for a goal
 * Allows creating rules based on: assignee, creator, OEM, stage, date range
 */
const GoalAssignmentRulesPanel = ({ goal, onClose, onRulesChanged }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableOems, setAvailableOems] = useState([]);
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState({
    rule_type: 'by_assignee',
    rule_value: {},
    description: '',
    is_inclusive: true,
    priority: 0
  });
  const [matchedOpportunities, setMatchedOpportunities] = useState(null);

  // Load rules and options on mount
  useEffect(() => {
    loadRules();
    loadOptions();
  }, [goal?.id]);

  // Load matched opportunities whenever rules change
  useEffect(() => {
    if (goal?.id) {
      loadMatchedOpportunities();
    }
  }, [rules]);

  const loadRules = async () => {
    if (!goal?.id) return;
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/goals/${goal.id}/rules`);
      setRules(response.data.data || []);
    } catch (error) {
      console.error('Error loading rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [usersRes, oemsRes] = await Promise.all([
        apiClient.get('/api/v1/goals/users/available'),
        apiClient.get('/api/v1/goals/oems/available')
      ]);
      setAvailableUsers(usersRes.data.data || []);
      setAvailableOems(oemsRes.data.data || []);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const loadMatchedOpportunities = async () => {
    if (!goal?.id) return;
    try {
      const response = await apiClient.get(`/api/v1/goals/${goal.id}/auto-matched`);
      setMatchedOpportunities(response.data.data);
    } catch (error) {
      console.error('Error loading matched opportunities:', error);
      setMatchedOpportunities(null);
    }
  };

  const handleAddRule = async () => {
    if (!goal?.id) return;
    try {
      setSaving(true);
      
      // Send as JSON body instead of query params
      await apiClient.post(`/api/v1/goals/${goal.id}/rules`, {
        rule_type: newRule.rule_type,
        rule_value: newRule.rule_value,
        description: newRule.description || null,
        is_inclusive: newRule.is_inclusive,
        priority: newRule.priority
      });
      
      // Reload rules
      await loadRules();
      setShowAddRule(false);
      setNewRule({
        rule_type: 'by_assignee',
        rule_value: {},
        description: '',
        is_inclusive: true,
        priority: 0
      });
      
      if (onRulesChanged) onRulesChanged();
    } catch (error) {
      console.error('Error adding rule:', error);
      alert('Failed to add rule: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await apiClient.delete(`/api/v1/goals/${goal.id}/rules/${ruleId}`);
      await loadRules();
      if (onRulesChanged) onRulesChanged();
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete rule');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await apiClient.put(`/api/v1/goals/${goal.id}/rules/${rule.id}`, null, {
        params: { is_active: !rule.is_active }
      });
      await loadRules();
      if (onRulesChanged) onRulesChanged();
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = cents / 100;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getRuleTypeLabel = (type) => {
    const labels = {
      'by_assignee': 'By Assigned User',
      'by_creator': 'By Creator',
      'by_oem': 'By OEM',
      'by_stage': 'By Stage',
      'by_date_range': 'By Date Range',
      'by_region': 'By Region',
      'by_revenue_min': 'By Minimum Revenue'
    };
    return labels[type] || type;
  };

  const getRuleIcon = (type) => {
    const icons = {
      'by_assignee': <Users className="w-4 h-4" />,
      'by_creator': <Users className="w-4 h-4" />,
      'by_oem': <Building2 className="w-4 h-4" />,
      'by_stage': <CheckCircle2 className="w-4 h-4" />,
      'by_date_range': <Calendar className="w-4 h-4" />,
      'by_revenue_min': <DollarSign className="w-4 h-4" />
    };
    return icons[type] || <Settings className="w-4 h-4" />;
  };

  const getRuleValueDisplay = (rule) => {
    const value = rule.rule_value || {};
    switch (rule.rule_type) {
      case 'by_assignee':
      case 'by_creator':
        const userIds = value.user_ids || [];
        const userNames = userIds.map(id => {
          const user = availableUsers.find(u => u.id === id);
          return user ? user.name : id.slice(0, 8);
        });
        return userNames.join(', ') || 'No users selected';
      case 'by_oem':
        return (value.oems || []).join(', ') || 'No OEMs selected';
      case 'by_stage':
        return (value.stages || []).join(', ') || 'No stages selected';
      case 'by_date_range':
        return `${value.start || '?'} to ${value.end || '?'}`;
      case 'by_revenue_min':
        return formatCurrency(value.min_revenue_cents);
      default:
        return JSON.stringify(value);
    }
  };

  const stages = ['target', 'qualified', 'rfq', 'quoted', 'awarded', 'sourced', 'lost'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Auto-Assignment Rules</h2>
            <p className="text-sm text-gray-600 mt-1">{goal?.name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Rules automatically match opportunities to this goal based on criteria
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary of Matched Opportunities */}
        {matchedOpportunities && (
          <div className="mx-6 mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-green-800">Auto-Matched Results</div>
                <div className="text-2xl font-bold text-green-600 mt-1">
                  {matchedOpportunities.summary?.matched_count || 0} opportunities
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Weighted Revenue</div>
                <div className="text-xl font-semibold text-gray-900">
                  {formatCurrency(matchedOpportunities.summary?.total_weighted_revenue_cents || 0)}
                </div>
                <div className="text-xs text-gray-500">
                  of {formatCurrency(goal?.target_revenue_cents || 0)} target
                </div>
              </div>
            </div>
            {matchedOpportunities.summary && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(matchedOpportunities.summary.revenue_progress_percent || 0, 100)}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {matchedOpportunities.summary.revenue_progress_percent || 0}% of target
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rules List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No auto-assignment rules configured.</p>
              <p className="text-sm mt-1">Add rules to automatically match opportunities to this goal.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    rule.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${rule.is_inclusive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                      {getRuleIcon(rule.rule_type)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {getRuleTypeLabel(rule.rule_type)}
                        {!rule.is_inclusive && <span className="ml-2 text-xs text-red-600">(EXCLUDE)</span>}
                      </div>
                      <div className="text-sm text-gray-600">{getRuleValueDisplay(rule)}</div>
                      {rule.description && (
                        <div className="text-xs text-gray-400 mt-1">{rule.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleRule(rule)}
                      className={`px-3 py-1 text-xs rounded ${
                        rule.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {rule.is_active ? 'Active' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Rule Form */}
          {showAddRule && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-4">Add New Rule</h4>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Rule Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Type</label>
                  <select
                    value={newRule.rule_type}
                    onChange={(e) => setNewRule({ ...newRule, rule_type: e.target.value, rule_value: {} })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="by_assignee">By Assigned User</option>
                    <option value="by_creator">By Creator</option>
                    <option value="by_oem">By OEM</option>
                    <option value="by_stage">By Stage</option>
                    <option value="by_date_range">By Date Range</option>
                    <option value="by_revenue_min">By Minimum Revenue</option>
                  </select>
                </div>

                {/* Include/Exclude */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                  <select
                    value={newRule.is_inclusive ? 'include' : 'exclude'}
                    onChange={(e) => setNewRule({ ...newRule, is_inclusive: e.target.value === 'include' })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="include">Include matching</option>
                    <option value="exclude">Exclude matching</option>
                  </select>
                </div>
              </div>

              {/* Rule Value - Dynamic based on type */}
              <div className="mt-4">
                {newRule.rule_type === 'by_assignee' || newRule.rule_type === 'by_creator' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Users
                    </label>
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                      {availableUsers.map((user) => (
                        <label key={user.id} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={(newRule.rule_value.user_ids || []).includes(user.id)}
                            onChange={(e) => {
                              const currentIds = newRule.rule_value.user_ids || [];
                              const newIds = e.target.checked
                                ? [...currentIds, user.id]
                                : currentIds.filter(id => id !== user.id);
                              setNewRule({
                                ...newRule,
                                rule_value: { ...newRule.rule_value, user_ids: newIds }
                              });
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{user.name}</span>
                          <span className="text-xs text-gray-400">({user.role})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : newRule.rule_type === 'by_oem' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select OEMs</label>
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                      {availableOems.map((oem) => (
                        <label key={oem} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={(newRule.rule_value.oems || []).includes(oem)}
                            onChange={(e) => {
                              const current = newRule.rule_value.oems || [];
                              const updated = e.target.checked
                                ? [...current, oem]
                                : current.filter(o => o !== oem);
                              setNewRule({
                                ...newRule,
                                rule_value: { ...newRule.rule_value, oems: updated }
                              });
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{oem}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : newRule.rule_type === 'by_stage' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Stages</label>
                    <div className="flex flex-wrap gap-2">
                      {stages.map((stage) => (
                        <label
                          key={stage}
                          className={`flex items-center gap-2 px-3 py-1 rounded-full border cursor-pointer ${
                            (newRule.rule_value.stages || []).includes(stage)
                              ? 'bg-blue-100 border-blue-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={(newRule.rule_value.stages || []).includes(stage)}
                            onChange={(e) => {
                              const current = newRule.rule_value.stages || [];
                              const updated = e.target.checked
                                ? [...current, stage]
                                : current.filter(s => s !== stage);
                              setNewRule({
                                ...newRule,
                                rule_value: { ...newRule.rule_value, stages: updated }
                              });
                            }}
                            className="hidden"
                          />
                          <span className="text-sm capitalize">{stage}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : newRule.rule_type === 'by_date_range' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={newRule.rule_value.start || ''}
                        onChange={(e) => setNewRule({
                          ...newRule,
                          rule_value: { ...newRule.rule_value, start: e.target.value, field: 'created_at' }
                        })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={newRule.rule_value.end || ''}
                        onChange={(e) => setNewRule({
                          ...newRule,
                          rule_value: { ...newRule.rule_value, end: e.target.value, field: 'created_at' }
                        })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                ) : newRule.rule_type === 'by_revenue_min' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Revenue ($)</label>
                    <input
                      type="number"
                      value={(newRule.rule_value.min_revenue_cents || 0) / 100}
                      onChange={(e) => setNewRule({
                        ...newRule,
                        rule_value: { min_revenue_cents: Math.round(parseFloat(e.target.value || 0) * 100) }
                      })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Enter minimum revenue"
                    />
                  </div>
                ) : null}
              </div>

              {/* Description */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={newRule.description}
                  onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Include all John's opportunities"
                />
              </div>

              {/* Actions */}
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowAddRule(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRule}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add Rule
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          {!showAddRule && (
            <button
              onClick={() => setShowAddRule(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Rule
            </button>
          )}
          <div className={!showAddRule ? '' : 'ml-auto'}>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalAssignmentRulesPanel;
